import { getDownloadURL, ref } from "firebase/storage";

const URL_CACHE_PREFIX = "storage_url_cache_v2:";
const META_CACHE_PREFIX = "storage_meta_cache_v2:";

const DEFAULT_URL_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 días
const DEFAULT_META_TTL_MS = 60 * 60 * 1000; // 1 hora

const memoryUrlCache = new Map();
const memoryMetaCache = new Map();
const pendingUrlRequests = new Map();

function makeKey(prefix, path, version = "") {
  return `${prefix}${path}::${version || "no-version"}`;
}

function safeGetLocalStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetLocalStorage(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // localStorage puede estar lleno, bloqueado o deshabilitado.
  }
}

function safeRemoveLocalStorage(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignorar.
  }
}

function parseJson(raw) {
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function isExpired(entry, ttlMs) {
  if (!entry?.cachedAt) return true;
  return Date.now() - Number(entry.cachedAt) > ttlMs;
}

export function appendVersionToUrl(url, version) {
  if (!url || !version) return url;

  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}v=${encodeURIComponent(version)}`;
}

export function getCachedStorageUrl(
  path,
  version = "",
  ttlMs = DEFAULT_URL_TTL_MS
) {
  if (!path) return "";

  const key = makeKey(URL_CACHE_PREFIX, path, version);
  const memoryEntry = memoryUrlCache.get(key);

  if (memoryEntry && !isExpired(memoryEntry, ttlMs)) {
    return appendVersionToUrl(memoryEntry.url, version);
  }

  const entry = parseJson(safeGetLocalStorage(key));

  if (!entry?.url || isExpired(entry, ttlMs)) {
    safeRemoveLocalStorage(key);
    memoryUrlCache.delete(key);
    return "";
  }

  memoryUrlCache.set(key, entry);
  return appendVersionToUrl(entry.url, version);
}

export function setCachedStorageUrl(path, url, version = "") {
  if (!path || !url) return;

  const key = makeKey(URL_CACHE_PREFIX, path, version);

  const entry = {
    url,
    cachedAt: Date.now(),
  };

  memoryUrlCache.set(key, entry);
  safeSetLocalStorage(key, JSON.stringify(entry));
}

export function removeCachedStorageUrl(path) {
  if (!path) return;

  [...memoryUrlCache.keys()].forEach((key) => {
    if (key.startsWith(`${URL_CACHE_PREFIX}${path}::`)) {
      memoryUrlCache.delete(key);
      safeRemoveLocalStorage(key);
    }
  });

  try {
    const keysToRemove = [];

    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);

      if (
        key?.startsWith(`${URL_CACHE_PREFIX}${path}::`) ||
        key === `storage_url_cache:${path}` ||
        key === `storage_url_cache_v1:${path}`
      ) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Ignorar.
  }
}

export async function getCachedDownloadUrl(storage, path, options = {}) {
  const cleanPath = String(path || "").trim();

  if (!cleanPath) return "";

  const version = String(options.version || "");
  const ttlMs = options.ttlMs || DEFAULT_URL_TTL_MS;

  const cachedUrl = getCachedStorageUrl(cleanPath, version, ttlMs);

  if (cachedUrl) return cachedUrl;

  const requestKey = makeKey(URL_CACHE_PREFIX, cleanPath, version);

  if (pendingUrlRequests.has(requestKey)) {
    return pendingUrlRequests.get(requestKey);
  }

  const promise = getDownloadURL(ref(storage, cleanPath))
    .then((url) => {
      setCachedStorageUrl(cleanPath, url, version);
      return appendVersionToUrl(url, version);
    })
    .finally(() => {
      pendingUrlRequests.delete(requestKey);
    });

  pendingUrlRequests.set(requestKey, promise);
  return promise;
}

export function getCachedStorageMeta(path, ttlMs = DEFAULT_META_TTL_MS) {
  if (!path) return null;

  const key = makeKey(META_CACHE_PREFIX, path);
  const memoryEntry = memoryMetaCache.get(key);

  if (memoryEntry && !isExpired(memoryEntry, ttlMs)) {
    return memoryEntry.metadata;
  }

  const entry = parseJson(safeGetLocalStorage(key));

  if (!entry?.metadata || isExpired(entry, ttlMs)) {
    safeRemoveLocalStorage(key);
    memoryMetaCache.delete(key);
    return null;
  }

  memoryMetaCache.set(key, entry);
  return entry.metadata;
}

export function setCachedStorageMeta(path, metadata) {
  if (!path || !metadata) return;

  const key = makeKey(META_CACHE_PREFIX, path);

  const safeMetadata = {
    name: metadata.name || "",
    fullPath: metadata.fullPath || path,
    size: metadata.size || 0,
    contentType: metadata.contentType || "",
    updated: metadata.updated || "",
    cacheControl: metadata.cacheControl || "",
  };

  const entry = {
    metadata: safeMetadata,
    cachedAt: Date.now(),
  };

  memoryMetaCache.set(key, entry);
  safeSetLocalStorage(key, JSON.stringify(entry));
}

export function removeCachedStorageMeta(path) {
  if (!path) return;

  const key = makeKey(META_CACHE_PREFIX, path);

  memoryMetaCache.delete(key);
  safeRemoveLocalStorage(key);
}

export function clearStorageExplorerCache() {
  memoryUrlCache.clear();
  memoryMetaCache.clear();
  pendingUrlRequests.clear();

  try {
    const keysToRemove = [];

    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);

      if (
        key?.startsWith(URL_CACHE_PREFIX) ||
        key?.startsWith(META_CACHE_PREFIX) ||
        key?.startsWith("storage_url_cache:") ||
        key?.startsWith("storage_url_cache_v1:") ||
        key?.startsWith("storage_meta_cache_v1:")
      ) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Ignorar.
  }
}